import React from 'react';
import { ui, tabStyle, STATUS_LABEL } from './ui';
import useBuilder from './useBuilder';
import PreviewPane from './PreviewPane';
import SettingsPanel from './SettingsPanel';
import SectionsPanel from './SectionsPanel';
import IngestPanel from './IngestPanel';
import PapersList from './PapersList';
import MasterHeaderPanel from './MasterHeaderPanel';

// Hybrid AI + manual Question Paper Builder. Used by TeacherDashboard and PrincipalDashboard.
// Teachers get scoped papers + "submit for approval"; principals get school-wide moderation + master header.
function QuestionPaperBuilder({ user, isPrincipal = false, onClose }) {
  const b = useBuilder(user, isPrincipal);
  const { view, setView, tab, setTab, paper, meta, locked, mismatch, autoTotal } = b;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: '#f1f5f9', display: 'flex', flexDirection: 'column' }}>
      <div style={{ ...ui.row, background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '6px 12px', flexWrap: 'nowrap', overflowX: 'auto' }}>
        <b style={{ color: '#3730a3', whiteSpace: 'nowrap' }}>{isPrincipal ? '📑 Examination & Paper Control Center' : '📝 Question Paper Builder'}</b>
        <button type="button" style={tabStyle(view === 'papers')} onClick={() => setView('papers')}>{isPrincipal ? 'All Papers' : 'My Papers'}</button>
        <button type="button" style={tabStyle(view === 'editor')} onClick={() => (paper ? setView('editor') : b.newPaper())}>Editor</button>
        {isPrincipal && <button type="button" style={tabStyle(view === 'master')} onClick={() => setView('master')}>Master Header</button>}
        <button type="button" style={{ ...ui.btnPrimary, marginLeft: 'auto', whiteSpace: 'nowrap' }} onClick={b.newPaper}>+ New paper</button>
        {onClose && <button type="button" style={ui.btnDanger} onClick={onClose}>✕ Close</button>}
      </div>

      {b.msg.text && <div style={{ ...(b.msg.type === 'err' ? ui.err : ui.ok), margin: '6px 12px' }}>{b.msg.text}</div>}

      {view === 'papers' && <div style={{ padding: 12, overflow: 'auto', flex: 1 }}><PapersList user={user} isPrincipal={isPrincipal} onOpen={b.openPaper} refreshKey={b.refresh} /></div>}
      {view === 'master' && isPrincipal && <div style={{ padding: 12, overflow: 'auto', flex: 1, maxWidth: 640 }}><MasterHeaderPanel user={user} onSaved={b.setMaster} /></div>}

      {view === 'editor' && paper && (
        <div style={{ display: 'flex', flex: 1, minHeight: 0, flexWrap: 'nowrap', overflow: 'hidden' }}>
          <div style={{ flex: '1 1 440px', minWidth: 0, maxWidth: 640, height: '100%', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', padding: 10 }}>
            <div style={{ ...ui.row, marginBottom: 8 }}>
              <b style={{ fontSize: 13 }}>Total: {autoTotal}{paper.maxMarksLocked ? ` / ${paper.maxMarks}` : ''}</b>
              {mismatch && <span style={{ color: '#b91c1c', fontSize: 12, fontWeight: 700 }}>⚠ {autoTotal > paper.maxMarks ? 'Over' : 'Under'} by {Math.abs(autoTotal - paper.maxMarks)}</span>}
              <span style={{ fontSize: 12, color: '#64748b' }}>Status: {STATUS_LABEL[meta.status]}{meta.readyForPrint ? ' · Ready for print' : ''}</span>
            </div>
            {locked && <div style={ui.err}>This paper is approved. Only the principal can change it — you can still download it.</div>}
            <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: 8 }}>
              {[['setup', 'Setup'], ['sections', 'Sections & Questions'], ['ingest', 'AI / Paste']].map(([k, l]) => (
                <button key={k} type="button" style={tabStyle(tab === k)} onClick={() => setTab(k)}>{l}</button>
              ))}
            </div>
            <fieldset disabled={locked} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
              {tab === 'setup' && <SettingsPanel paper={paper} setPaper={b.setPaper} scope={b.scope} autoTotal={autoTotal} />}
              {tab === 'sections' && <SectionsPanel sections={paper.sections} setSections={b.setSections} canAI={b.aiConfigured} onRegen={b.regen} busyId={b.busyId} />}
              {tab === 'ingest' && (
                <IngestPanel user={user} paper={paper} setSections={b.setSections} aiConfigured={b.aiConfigured}
                  topics={b.topics} setTopics={b.setTopics} difficulty={b.difficulty} setDifficulty={b.setDifficulty} />
              )}
            </fieldset>
            <div style={{ ...ui.card, position: 'sticky', bottom: 0, marginTop: 12 }}>
              <div style={ui.row}>
                <button type="button" style={ui.btnPrimary} disabled={b.saving || locked} onClick={() => b.save(meta.status)}>{b.saving ? 'Saving…' : '💾 Save'}</button>
                {!isPrincipal && <button type="button" style={ui.btn} disabled={b.saving || locked} onClick={() => b.save('pending_approval')}>📤 Submit for Principal Approval</button>}
                <label style={{ fontSize: 12 }}><input type="checkbox" checked={meta.readyForPrint} disabled={locked} onChange={(e) => b.toggleReady(e.target.checked)} /> Ready for print</label>
                {isPrincipal && <button type="button" style={meta.status === 'approved' ? ui.btn : ui.btnGreen} onClick={() => b.approve(meta.status !== 'approved')}>{meta.status === 'approved' ? 'Revoke approval' : '✔ Approve for Exam'}</button>}
              </div>
            </div>
          </div>
          <PreviewPane paper={paper} onError={(t) => b.flash('err', t)} />
        </div>
      )}
    </div>
  );
}

export default QuestionPaperBuilder;
